import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, Toaster } from 'sonner';
import BobineLogo from '../components/BobineLogo';
import UniversAdminTab from '../components/admin/UniversAdminTab';
import TestimonialsAdminTab from '../components/admin/TestimonialsAdminTab';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;

const CATEGORY_LABELS = { nostalgie: 'Nostalgie', calendrier: 'Calendrier', emotion: 'Douceur', engagement: 'Engagement', custom: 'Personnalise' };
const CATEGORY_COLORS = { nostalgie: '#bfa76a', calendrier: '#6a9fbf', emotion: '#bf6a9f', engagement: '#6abf8a', custom: '#D4AF37' };

const MurmuresLibrary = ({ adminKey, murmureText, setMurmureText }) => {
  const [phrases, setPhrases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(null);
  const [newText, setNewText] = useState('');
  const [newCategory, setNewCategory] = useState('custom');

  const fetchPhrases = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/murmures`, { headers: { 'X-Admin-Key': adminKey } });
      if (res.ok) { const d = await res.json(); setPhrases(d.murmures || []); }
    } catch {}
    setLoading(false);
  };

  useEffect(() => { if (adminKey) fetchPhrases(); }, [adminKey]);

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setMurmureText(text);
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  const deleteMurmure = async (id) => {
    await fetch(`${API}/admin/murmures/${id}`, { method: 'DELETE', headers: { 'X-Admin-Key': adminKey } });
    setPhrases(p => p.filter(m => m.id !== id));
  };

  const addMurmure = async () => {
    if (!newText.trim()) return;
    const res = await fetch(`${API}/admin/murmures`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
      body: JSON.stringify({ text: newText.trim(), category: newCategory }),
    });
    if (res.ok) { setNewText(''); fetchPhrases(); }
  };

  const grouped = {};
  phrases.forEach(p => { const c = p.category || 'custom'; if (!grouped[c]) grouped[c] = []; grouped[c].push(p); });

  return (
    <div data-testid="murmures-library" style={{ borderRadius: 12, border: '1px solid rgba(212,175,55,0.1)', overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '14px 16px', borderBottom: '1px solid rgba(212,175,55,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 600 }}>
          Bibliotheque ({phrases.length} murmures)
        </span>
      </div>

      {loading ? (
        <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)', padding: 16, textAlign: 'center' }}>Chargement...</p>
      ) : (
        Object.entries(grouped).map(([cat, items]) => (
          <div key={cat}>
            <div style={{ padding: '8px 16px', backgroundColor: 'rgba(212,175,55,0.03)', borderBottom: '1px solid rgba(212,175,55,0.06)' }}>
              <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: CATEGORY_COLORS[cat] || '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, fontWeight: 600 }}>
                {CATEGORY_LABELS[cat] || cat} ({items.length})
              </span>
            </div>
            {items.map((m) => (
              <div key={m.id} data-testid={`murmure-${m.id}`} style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px',
                borderBottom: '1px solid rgba(212,175,55,0.04)',
              }}>
                <p style={{ flex: 1, fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)', margin: 0, lineHeight: 1.6, fontStyle: 'italic' }}>
                  "{m.text}"
                </p>
                <button data-testid={`copy-${m.id}`} onClick={() => copyToClipboard(m.text, m.id)} title="Copier dans le champ d'envoi"
                  style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontFamily: "'Georgia', serif", cursor: 'pointer', backgroundColor: copied === m.id ? 'rgba(39,174,96,0.15)' : 'rgba(212,175,55,0.08)', color: copied === m.id ? '#27ae60' : '#D4AF37', border: `1px solid ${copied === m.id ? 'rgba(39,174,96,0.3)' : 'rgba(212,175,55,0.15)'}`, transition: 'all 0.2s' }}>
                  {copied === m.id ? 'Copie !' : 'Copier'}
                </button>
                <button data-testid={`delete-${m.id}`} onClick={() => deleteMurmure(m.id)} title="Supprimer"
                  style={{ flexShrink: 0, padding: '4px 10px', borderRadius: 6, fontSize: 11, fontFamily: "'Georgia', serif", cursor: 'pointer', backgroundColor: 'rgba(192,57,43,0.08)', color: 'rgba(192,57,43,0.7)', border: '1px solid rgba(192,57,43,0.15)' }}>
                  Suppr.
                </button>
              </div>
            ))}
          </div>
        ))
      )}

      {/* Add new murmure */}
      <div style={{ padding: '12px 16px', borderTop: '1px solid rgba(212,175,55,0.08)', display: 'flex', gap: 8, alignItems: 'center' }}>
        <select value={newCategory} onChange={e => setNewCategory(e.target.value)}
          style={{ fontFamily: "'Georgia', serif", fontSize: 11, padding: '6px 8px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#D4AF37' }}>
          {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <input value={newText} onChange={e => setNewText(e.target.value)} placeholder="Ajouter un nouveau murmure..."
          onKeyDown={e => { if (e.key === 'Enter') addMurmure(); }}
          style={{ flex: 1, fontFamily: "'Georgia', serif", fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }} />
        <button onClick={addMurmure} data-testid="add-murmure-btn"
          style={{ fontFamily: "'Georgia', serif", fontSize: 11, padding: '6px 14px', borderRadius: 6, backgroundColor: 'rgba(212,175,55,0.1)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.2)', cursor: 'pointer' }}>+ Ajouter</button>
      </div>
    </div>
  );
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [adminKey, setAdminKey] = useState(() => sessionStorage.getItem('bobine_admin_key') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [pricing, setPricing] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState(null);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('stats');
  const [reports, setReports] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [salesView, setSalesView] = useState('monthly');
  const [storageStatus, setStorageStatus] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrateResult, setMigrateResult] = useState(null);

  // Marketing state
  const [banner, setBanner] = useState({ message: '', style: 'info', link_text: '', link_url: '', active: true });
  const [bannerSaving, setBannerSaving] = useState(false);
  const [bannerMsg, setBannerMsg] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [couponForm, setCouponForm] = useState({ name: '', discount_type: 'percent', discount_value: '', currency: 'eur', duration: 'once', duration_months: '', max_redemptions: '' });
  const [couponSaving, setCouponSaving] = useState(false);
  const [murmures, setMurmures] = useState([]);
  const [murmureText, setMurmureText] = useState('');
  const [murmureSending, setMurmureSending] = useState(false);
  const [autoConfig, setAutoConfig] = useState({ enabled: false, frequency_days: 7, messages: [] });
  const [autoMsg, setAutoMsg] = useState('');
  const [autoSaving, setAutoSaving] = useState(false);

  // Automates state
  const [schedulerData, setSchedulerData] = useState(null);
  const [schedulerSettings, setSchedulerSettings] = useState({});
  const [schedulerSaving, setSchedulerSaving] = useState(false);
  const [schedulerMsg, setSchedulerMsg] = useState(null);

  // Editable state
  const [productPrices, setProductPrices] = useState({});
  const [planPrices, setPlanPrices] = useState({});
  const [libertePacks, setLibertePacks] = useState({});
  const [optionPrices, setOptionPrices] = useState({});
  const [settings, setSettings] = useState({ lignees_discount: 20, liberte_free_restorations: 5 });

  const fetchData = async (key) => {
    setLoading(true);
    setError(null);
    try {
      const headers = { 'X-Admin-Key': key };
      // Sync Stripe revenue silencieusement avant de récupérer les stats
      // (no-op si pas de nouvelles factures payées) — timeout 8s pour ne pas bloquer
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 8000);
        await fetch(`${API}/admin/sync-stripe-revenue`, { method: 'POST', headers, signal: ctrl.signal });
        clearTimeout(t);
      } catch {}
      const [pricingRes, statsRes] = await Promise.all([
        fetch(`${API}/admin/pricing`, { headers }),
        fetch(`${API}/admin/stats`, { headers }),
      ]);
      if (!pricingRes.ok) {
        if (pricingRes.status === 403) throw new Error('Mot de passe incorrect');
        throw new Error(`Erreur ${pricingRes.status}`);
      }
      const pricingData = await pricingRes.json();
      setPricing(pricingData.pricing);
      setAuthenticated(true);
      sessionStorage.setItem('bobine_admin_key', key);

      // Products (livres)
      const pp = {};
      for (const [id, info] of Object.entries(pricingData.pricing.products)) pp[id] = info.current_price;
      setProductPrices(pp);

      // Plans
      const pl = {};
      for (const [id, info] of Object.entries(pricingData.pricing.plans)) pl[id] = info.current_price;
      setPlanPrices(pl);

      // Liberté packs
      const lp = {};
      for (const [id, info] of Object.entries(pricingData.pricing.liberte_packs)) {
        lp[id] = { price: info.current_price, photos: info.current_photos };
      }
      setLibertePacks(lp);

      // Options
      const op = {};
      for (const [id, info] of Object.entries(pricingData.pricing.options)) op[id] = info.current_price;
      setOptionPrices(op);

      // Settings
      setSettings(pricingData.pricing.settings);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.stats);
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (e) => { e.preventDefault(); fetchData(adminKey); };

  const fetchReports = async () => {
    setReportsLoading(true);
    try {
      const res = await fetch(`${API}/admin/reports`, { headers: { 'X-Admin-Key': adminKey } });
      if (res.ok) setReports((await res.json()).reports);
    } catch (e) { console.error(e); }
    finally { setReportsLoading(false); }
  };

  const fetchStorageStatus = async () => {
    try {
      const res = await fetch(`${API}/storage/status`);
      if (res.ok) setStorageStatus(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchSchedulers = async () => {
    try {
      const [schedRes, settingsRes] = await Promise.all([
        fetch(`${API}/admin/schedulers`, { headers: { 'X-Admin-Key': adminKey } }),
        fetch(`${API}/admin/settings`, { headers: { 'X-Admin-Key': adminKey } }),
      ]);
      if (schedRes.ok) setSchedulerData(await schedRes.json());
      if (settingsRes.ok) {
        const d = await settingsRes.json();
        setSchedulerSettings(d.settings || {});
      }
    } catch (e) { console.error(e); }
  };

  const saveSchedulerSettings = async () => {
    setSchedulerSaving(true); setSchedulerMsg(null);
    try {
      const res = await fetch(`${API}/admin/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(schedulerSettings),
      });
      if (res.ok) setSchedulerMsg('Parametres sauvegardes');
      else setSchedulerMsg('Erreur de sauvegarde');
    } catch (e) { setSchedulerMsg('Erreur reseau'); }
    finally { setSchedulerSaving(false); setTimeout(() => setSchedulerMsg(null), 3000); }
  };

  // ── Marketing functions ──
  const fetchMarketing = async () => {
    const h = { 'X-Admin-Key': adminKey };
    try {
      const [bannerRes, couponsRes, murmuresRes, autoRes] = await Promise.all([
        fetch(`${API}/marketing/banner`),
        fetch(`${API}/marketing/coupons`, { headers: h }),
        fetch(`${API}/marketing/murmures`, { headers: h }),
        fetch(`${API}/marketing/murmure/auto`, { headers: h }),
      ]);
      if (bannerRes.ok) {
        const b = await bannerRes.json();
        if (b.active) setBanner({ message: b.message || '', style: b.style || 'info', link_text: b.link_text || '', link_url: b.link_url || '', active: true });
      }
      if (couponsRes.ok) { const d = await couponsRes.json(); setCoupons(d.coupons || []); }
      if (murmuresRes.ok) { const d = await murmuresRes.json(); setMurmures(d.murmures || []); }
      if (autoRes.ok) { const d = await autoRes.json(); if (d.enabled !== undefined) setAutoConfig(d); }
    } catch (e) { console.error(e); }
  };

  const saveBanner = async () => {
    setBannerSaving(true); setBannerMsg(null);
    try {
      const res = await fetch(`${API}/marketing/banner`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(banner),
      });
      if (res.ok) setBannerMsg('Bandeau mis a jour');
      else setBannerMsg('Erreur');
    } catch { setBannerMsg('Erreur'); }
    setBannerSaving(false);
  };

  const disableBanner = async () => {
    await fetch(`${API}/marketing/banner`, { method: 'DELETE', headers: { 'X-Admin-Key': adminKey } });
    setBanner({ ...banner, active: false, message: '' }); setBannerMsg('Bandeau desactive');
  };

  const createCoupon = async () => {
    setCouponSaving(true);
    try {
      const payload = {
        name: couponForm.name,
        discount_type: couponForm.discount_type,
        discount_value: parseFloat(couponForm.discount_value),
        currency: couponForm.currency,
        duration: couponForm.duration,
      };
      if (couponForm.duration === 'repeating' && couponForm.duration_months) payload.duration_months = parseInt(couponForm.duration_months);
      if (couponForm.max_redemptions) payload.max_redemptions = parseInt(couponForm.max_redemptions);
      const res = await fetch(`${API}/marketing/coupons`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify(payload),
      });
      if (res.ok) { setCouponForm({ name: '', discount_type: 'percent', discount_value: '', currency: 'eur', duration: 'once', duration_months: '', max_redemptions: '' }); fetchMarketing(); }
    } catch { /* silent */ }
    setCouponSaving(false);
  };

  const deleteCoupon = async (id) => {
    await fetch(`${API}/marketing/coupons/${id}`, { method: 'DELETE', headers: { 'X-Admin-Key': adminKey } });
    setCoupons(coupons.filter(c => c.id !== id));
  };

  const createMurmure = async (sendNow) => {
    if (!murmureText.trim()) return;
    setMurmureSending(true);
    const toastStyle = { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' };
    try {
      const res = await fetch(`${API}/marketing/murmures`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({ message: murmureText.trim(), send_now: sendNow }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Échec de l\'envoi', { style: toastStyle, duration: 4000 });
      } else if (sendNow) {
        const sent = data?.murmure?.emails_sent ?? null;
        if (sent === null) {
          toast.success('Murmure activé in-app', { style: toastStyle, duration: 3500 });
        } else if (sent === 0) {
          toast.warning('Murmure activé in-app, mais 0 email envoyé (vérifiez le statut Resend du domaine)', { style: toastStyle, duration: 5500 });
        } else {
          toast.success(`Murmure envoyé · ${sent} email${sent > 1 ? 's' : ''} parti${sent > 1 ? 's' : ''}`, { style: toastStyle, duration: 4000 });
        }
      } else {
        toast.success('Murmure ajouté à la bibliothèque', { style: toastStyle, duration: 3000 });
      }
      setMurmureText(''); fetchMarketing();
    } catch (e) {
      toast.error(e?.message || 'Erreur réseau', { style: toastStyle, duration: 4000 });
    }
    setMurmureSending(false);
  };

  const sendMurmure = async (id) => {
    const toastStyle = { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' };
    try {
      const res = await fetch(`${API}/marketing/murmures/${id}/send`, {
        method: 'POST', headers: { 'X-Admin-Key': adminKey },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data?.detail || 'Échec de l\'envoi', { style: toastStyle, duration: 4000 });
      } else {
        const sent = data?.emails_sent ?? data?.murmure?.emails_sent ?? null;
        if (sent === null || sent === undefined) {
          toast.success('Murmure activé', { style: toastStyle, duration: 3500 });
        } else if (sent === 0) {
          toast.warning('Murmure activé, mais 0 email envoyé (Resend pas encore vérifié ?)', { style: toastStyle, duration: 5500 });
        } else {
          toast.success(`Murmure envoyé · ${sent} email${sent > 1 ? 's' : ''} parti${sent > 1 ? 's' : ''}`, { style: toastStyle, duration: 4000 });
        }
      }
      fetchMarketing();
    } catch (e) {
      toast.error(e?.message || 'Erreur réseau', { style: toastStyle, duration: 4000 });
    }
  };

  const saveAutoConfig = async () => {
    setAutoSaving(true);
    await fetch(`${API}/marketing/murmure/auto`, {
      method: 'POST', headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
      body: JSON.stringify(autoConfig),
    });
    setAutoSaving(false);
  };

  const handleMigrate = async (all = false) => {
    setMigrating(true);
    setMigrateResult(null);
    try {
      const res = await fetch(`${API}/storage/migrate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_password: adminKey, batch_size: 20, migrate_all: all }),
      });
      const data = await res.json();
      if (res.ok) {
        setMigrateResult(data);
        // Poll for progress if it's a background task
        if (data.status === 'started' || data.status === 'already_running') {
          const pollInterval = setInterval(async () => {
            try {
              const prog = await fetch(`${API}/storage/migrate/progress`);
              const progData = await prog.json();
              setMigrateResult(prev => ({ ...prev, ...progData }));
              if (!progData.running) {
                clearInterval(pollInterval);
                setMigrating(false);
                fetchStorageStatus();
              }
            } catch (e) { /* ignore polling errors */ }
          }, 3000);
        } else {
          setMigrating(false);
          fetchStorageStatus();
        }
      } else {
        setMigrateResult({ status: 'error', message: data.detail || 'Erreur lors de la migration' });
        setMigrating(false);
      }
    } catch (e) { setMigrateResult({ status: 'error', message: e.message }); setMigrating(false); }
  };

  const handleCsvExport = async () => {
    try {
      const res = await fetch(`${API}/admin/reports/csv`, { headers: { 'X-Admin-Key': adminKey } });
      if (!res.ok) throw new Error('Erreur export');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = `bobine_rapports.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert('Erreur lors de l\'export CSV'); }
  };

  useEffect(() => {
    if (adminKey && !authenticated) fetchData(adminKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setSaveMsg(null);
    try {
      const res = await fetch(`${API}/admin/pricing`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'X-Admin-Key': adminKey },
        body: JSON.stringify({
          products: productPrices,
          plans: planPrices,
          liberte_packs: libertePacks,
          options: optionPrices,
          settings,
        }),
      });
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setSaveMsg({ type: 'success', text: `${data.updated.length} parametre(s) mis a jour` });
      await fetchData(adminKey);
    } catch (e) {
      setSaveMsg({ type: 'error', text: e.message });
    } finally {
      setSaving(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '10px 14px', borderRadius: 8,
    fontFamily: "'Georgia', serif", fontSize: 15,
    backgroundColor: 'rgba(212,175,55,0.04)',
    border: '1px solid rgba(212,175,55,0.25)',
    color: '#D4AF37', outline: 'none', textAlign: 'right',
  };
  const labelStyle = { fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.85)', display: 'block', marginBottom: 6 };
  const hintStyle = { fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.65)', marginTop: 4 };
  const sectionTitle = (text) => (
    <h3 style={{ fontFamily: "'Georgia', serif", fontSize: 20, fontWeight: 700, color: '#D4AF37', marginBottom: 20, paddingBottom: 10, borderBottom: '1px solid rgba(212,175,55,0.15)' }}>{text}</h3>
  );

  const planLabels = { eveil: "L'\u00c9veil", heritage: "L'H\u00e9ritage", liberte_500: "Libert\u00e9 500", liberte_1000: "Libert\u00e9 1000" };

  // Login
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#000' }}>
        <div style={{ maxWidth: 400, width: '90%', padding: '40px 32px', backgroundColor: '#0a0a0a', borderRadius: 16, border: '1px solid rgba(212,175,55,0.2)' }}>
          <div className="text-center" style={{ marginBottom: 32 }}>
            <BobineLogo size={24} />
            <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.5)', marginTop: 12 }}>Administration</p>
          </div>
          <form onSubmit={handleLogin}>
            <input data-testid="admin-password-input" type="password" value={adminKey} onChange={e => setAdminKey(e.target.value)} placeholder="Mot de passe administrateur" style={{ ...inputStyle, textAlign: 'left', marginBottom: 16 }} />
            {error && <p data-testid="admin-error" style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#c0392b', marginBottom: 12 }}>{error}</p>}
            <button data-testid="admin-login-button" type="submit" disabled={loading || !adminKey} style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 15, padding: '12px', borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', fontWeight: 600, opacity: loading ? 0.6 : 1 }}>
              {loading ? 'Connexion...' : 'Acceder'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#000' }}>
      <Toaster position="top-center" richColors={false} closeButton toastOptions={{ style: { fontFamily: "'Georgia', serif", backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: '#D4AF37' } }} />
      {/* Header */}
      <header style={{ borderBottom: '1px solid rgba(212,175,55,0.2)' }}>
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} data-testid="admin-back-to-site" style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', background: 'none', border: '1px solid rgba(212,175,55,0.4)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#D4AF37" strokeWidth="2"><polyline points="15 18 9 12 15 6" /></svg>
              Retour au site
            </button>
            <BobineLogo size={22} />
          </div>
          <div className="flex items-center gap-4">
            <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,175,55,0.5)' }}>Administration</span>
            <button onClick={() => { sessionStorage.removeItem('bobine_admin_key'); setAuthenticated(false); setAdminKey(''); }} data-testid="admin-logout" style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(212,175,55,0.4)', background: 'none', border: '1px solid rgba(212,175,55,0.2)', borderRadius: 6, padding: '6px 14px', cursor: 'pointer' }}>
              D{'\u00e9'}connexion
            </button>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="container mx-auto px-6 pt-6">
        <div className="flex justify-center gap-4" style={{ marginBottom: 32 }}>
          {[{ key: 'stats', label: 'Tableau de Bord' }, { key: 'reports', label: 'Rapports' }, { key: 'pricing', label: 'Pilotage des Prix' }, { key: 'marketing', label: 'Marketing' }, { key: 'univers', label: "L'Univers" }, { key: 'temoignages', label: 'Témoignages' }, { key: 'automates', label: 'Automates' }, { key: 'storage', label: 'Stockage' }].map(tab => (
            <button key={tab.key} data-testid={`admin-tab-${tab.key}`} onClick={() => { setActiveSection(tab.key); if (tab.key === 'reports' && !reports) fetchReports(); if (tab.key === 'storage' && !storageStatus) fetchStorageStatus(); if (tab.key === 'marketing') fetchMarketing(); if (tab.key === 'automates') fetchSchedulers(); }} style={{
              fontFamily: "'Georgia', serif", fontSize: 14, padding: '10px 24px', borderRadius: 8, cursor: 'pointer', transition: 'all 0.3s',
              border: activeSection === tab.key ? '1px solid #D4AF37' : '1px solid rgba(212,175,55,0.15)',
              backgroundColor: activeSection === tab.key ? 'rgba(212,175,55,0.08)' : 'transparent',
              color: activeSection === tab.key ? '#D4AF37' : 'rgba(212,175,55,0.5)',
            }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <main className="container mx-auto px-6 pb-10">
        <div className="max-w-4xl mx-auto">

          {/* ====== TABLEAU DE BORD ====== */}
          {activeSection === 'stats' && stats && (
            <div>
              <div data-testid="admin-tresors-global" style={{
                textAlign: 'center', marginBottom: 40, padding: '36px 24px',
                background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))',
                border: '1px solid rgba(212,175,55,0.2)', borderRadius: 16,
              }}>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 2, marginBottom: 8 }}>
                  Tresors Scelles dans Bobine
                </p>
                <p data-testid="admin-total-tresors" style={{ fontFamily: "'Georgia', serif", fontSize: 52, fontWeight: 400, color: '#D4AF37', lineHeight: 1, marginBottom: 12 }}>
                  {stats.tresors.total.toLocaleString('fr-FR')}
                </p>
                <div className="flex justify-center gap-8 flex-wrap" style={{ marginTop: 8 }}>
                  <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)' }}>{stats.tresors.restored} restaures</span>
                  <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)' }}>{stats.tresors.with_audio} avec recit vocal</span>
                </div>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.65)', marginTop: 12, fontStyle: 'italic' }}>
                  Coffre uniquement (hors Sas de Tri et restaurations)
                </p>
              </div>

              <div className="grid sm:grid-cols-3 gap-5" style={{ marginBottom: 36 }}>
                {/* Revenus */}
                <div data-testid="admin-revenue-card" style={{ backgroundColor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '24px 20px' }}>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Revenus totaux</p>
                  <p data-testid="admin-total-revenue" style={{ fontFamily: "'Georgia', serif", fontSize: 32, color: '#D4AF37', fontWeight: 300, marginBottom: 12, letterSpacing: -0.5 }}>
                    {stats.revenue.total.toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                    <span style={{ fontSize: 18, fontWeight: 300, color: 'rgba(212,175,55,0.4)', marginLeft: 6 }}>{'\u20AC'}</span>
                  </p>
                  <div style={{ borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 10 }}>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(247,230,189,0.95)', marginBottom: 4 }}>
                      Abonnements : {stats.revenue.subscriptions.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {'\u20AC'}
                    </p>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(247,230,189,0.95)' }}>
                      Livres : {stats.revenue.products.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} {'\u20AC'} ({stats.revenue.book_count} vendu{stats.revenue.book_count !== 1 ? 's' : ''})
                    </p>
                    <button
                      data-testid="admin-sync-stripe-btn"
                      onClick={async () => {
                        try {
                          const headers = { 'X-Admin-Key': sessionStorage.getItem('bobine_admin_key') || '' };
                          const res = await fetch(`${API}/admin/sync-stripe-revenue`, { method: 'POST', headers });
                          const data = await res.json();
                          if (data.status === 'success') {
                            await fetchData(headers['X-Admin-Key']);
                            alert(`Synchronisation r\u00e9ussie : ${data.synced} nouvelle(s) facture(s) import\u00e9e(s).`);
                          }
                        } catch (e) { alert('Erreur de synchronisation Stripe'); }
                      }}
                      style={{
                        marginTop: 12,
                        fontFamily: "'Georgia', serif",
                        fontSize: 11,
                        letterSpacing: 1,
                        textTransform: 'uppercase',
                        padding: '8px 14px',
                        borderRadius: 16,
                        cursor: 'pointer',
                        background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                        color: '#1a1100',
                        border: '1px solid #8B6914',
                        fontWeight: 600,
                      }}
                    >
                      Synchroniser Stripe
                    </button>
                  </div>
                </div>

                {/* Utilisateurs */}
                <div data-testid="admin-users-card" style={{ backgroundColor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '24px 20px' }}>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Utilisateurs</p>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 32, color: '#D4AF37', fontWeight: 400, marginBottom: 12 }}>{stats.users.total}</p>
                  <div style={{ borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 10 }}>
                    {Object.entries(stats.users.by_plan).sort((a, b) => b[1] - a[1]).map(([plan, count]) => (
                      <div key={plan} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)' }}>{planLabels[plan] || plan}</span>
                        <span data-testid={`admin-plan-count-${plan}`} style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', fontWeight: 600 }}>{count}</span>
                      </div>
                    ))}
                    <button
                      data-testid="admin-create-test-user-btn"
                      onClick={async () => {
                        try {
                          const headers = { 'X-Admin-Key': sessionStorage.getItem('bobine_admin_key') || '', 'Content-Type': 'application/json' };
                          const res = await fetch(`${API}/admin/create-test-user`, { method: 'POST', headers, body: JSON.stringify({}) });
                          const data = await res.json();
                          if (data.status === 'success') {
                            const lines = [
                              `Compte testeur cr\u00e9\u00e9 \u2728`,
                              ``,
                              `Email   : ${data.user.email}`,
                              `Password: ${data.user.password}`,
                              `Plan    : ${data.user.plan}`,
                              ``,
                              `Identifiants copi\u00e9s dans le presse-papiers.`,
                            ].join('\n');
                            try { await navigator.clipboard.writeText(`${data.user.email}\n${data.user.password}`); } catch {}
                            alert(lines);
                          } else {
                            alert('Erreur : ' + (data.detail || 'inconnue'));
                          }
                        } catch (e) { alert('Erreur de cr\u00e9ation: ' + e.message); }
                      }}
                      style={{
                        marginTop: 12, width: '100%',
                        fontFamily: "'Georgia', serif", fontSize: 11,
                        letterSpacing: 1, textTransform: 'uppercase',
                        padding: '8px 14px', borderRadius: 16, cursor: 'pointer',
                        background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
                        color: '#1a1100', border: '1px solid #8B6914', fontWeight: 600,
                      }}
                    >
                      + Cr\u00e9er un compte testeur
                    </button>
                  </div>
                </div>

                {/* Partage */}
                <div data-testid="admin-sharing-card" style={{ backgroundColor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '24px 20px' }}>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Fil de Soie</p>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 32, color: '#D4AF37', fontWeight: 400, marginBottom: 12 }}>
                    {stats.sharing.active_links} lien{stats.sharing.active_links !== 1 ? 's' : ''}
                  </p>
                  <div style={{ borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 10 }}>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)' }}>
                      {stats.sharing.total_views} visite{stats.sharing.total_views !== 1 ? 's' : ''} au total
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'stats' && !stats && (
            <p style={{ fontFamily: "'Georgia', serif", color: 'rgba(212,175,55,0.4)', textAlign: 'center' }}>Chargement...</p>
          )}

          {/* ====== RAPPORTS ====== */}
          {activeSection === 'reports' && (
            <div>
              {reportsLoading && <p style={{ fontFamily: "'Georgia', serif", color: 'rgba(212,175,55,0.4)', textAlign: 'center' }}>Chargement des rapports...</p>}

              {reports && (
                <>
                  {/* Export CSV */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                    <button data-testid="csv-export-btn" onClick={handleCsvExport} style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 24px', borderRadius: 8,
                      cursor: 'pointer', color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none', fontWeight: 600,
                      display: 'flex', alignItems: 'center', gap: 8,
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Exporter CSV
                    </button>
                  </div>

                  {/* Géographie */}
                  {sectionTitle('R\u00e9partition G\u00e9ographique')}
                  <div style={{ marginBottom: 40 }}>
                    <div style={{ backgroundColor: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 0, padding: '12px 20px', borderBottom: '1px solid rgba(212,175,55,0.12)' }}>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5 }}>Pays</span>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'right', minWidth: 80 }}>Utilisateurs</span>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, textAlign: 'right', minWidth: 60 }}>%</span>
                      </div>
                      {reports.geography.map((g, i) => {
                        const totalGeo = reports.geography.reduce((s, x) => s + x.users, 0);
                        return (
                          <div key={i} data-testid={`geo-row-${i}`} style={{
                            display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 0, padding: '10px 20px',
                            borderBottom: i < reports.geography.length - 1 ? '1px solid rgba(212,175,55,0.06)' : 'none',
                          }}>
                            <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(245,245,220,0.9)' }}>
                              {g.country_code && <span style={{ marginRight: 8 }}>{g.country_code}</span>}{g.country}
                            </span>
                            <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 600, textAlign: 'right', minWidth: 80 }}>{g.users}</span>
                            <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.85)', textAlign: 'right', minWidth: 60 }}>{(g.users / totalGeo * 100).toFixed(0)}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Ventes */}
                  {sectionTitle('Suivi des Ventes')}
                  <div style={{ marginBottom: 40 }}>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                      {[{k: 'weekly', l: 'Semaine'}, {k: 'monthly', l: 'Mois'}, {k: 'yearly', l: 'Ann\u00e9e'}].map(v => (
                        <button key={v.k} data-testid={`sales-view-${v.k}`} onClick={() => setSalesView(v.k)} style={{
                          fontFamily: "'Georgia', serif", fontSize: 12, padding: '6px 16px', borderRadius: 6, cursor: 'pointer',
                          border: salesView === v.k ? '1px solid #D4AF37' : '1px solid rgba(212,175,55,0.15)',
                          backgroundColor: salesView === v.k ? 'rgba(212,175,55,0.08)' : 'transparent',
                          color: salesView === v.k ? '#D4AF37' : 'rgba(212,175,55,0.5)',
                        }}>{v.l}</button>
                      ))}
                    </div>
                    <div style={{ backgroundColor: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, overflow: 'hidden' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0, padding: '12px 20px', borderBottom: '1px solid rgba(212,175,55,0.12)' }}>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1 }}>P{'\u00e9'}riode</span>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'right' }}>Abonnements</span>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'right' }}>Achats</span>
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1, textAlign: 'right' }}>Total</span>
                      </div>
                      {(reports.sales[salesView] || []).length === 0 && (
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.75)', textAlign: 'center', padding: 20 }}>Aucune vente</p>
                      )}
                      {(reports.sales[salesView] || []).map((s, i) => (
                        <div key={i} data-testid={`sales-row-${i}`} style={{
                          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0, padding: '10px 20px',
                          borderBottom: i < reports.sales[salesView].length - 1 ? '1px solid rgba(212,175,55,0.06)' : 'none',
                        }}>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.85)' }}>{s.period}</span>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.7)', textAlign: 'right' }}>{s.subscriptions.toFixed(2)} {'\u20AC'}</span>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.7)', textAlign: 'right' }}>{s.one_time.toFixed(2)} {'\u20AC'}</span>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 300, textAlign: 'right' }}>{s.total.toFixed(2)} {'\u20AC'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* KPIs */}
                  {sectionTitle('Indicateurs de Performance')}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5" style={{ marginBottom: 40 }}>
                    {[
                      { label: 'Utilisateurs', value: reports.kpis.total_users, sub: `${reports.kpis.active_users_7d} actifs (7j)` },
                      { label: 'Photos stock\u00e9es', value: reports.kpis.total_photos.toLocaleString('fr-FR'), sub: `dont coffre, sas et restaurations` },
                      { label: '\u00c9claireurs invit\u00e9s', value: reports.kpis.total_eclaireur_invitations, sub: `${reports.kpis.avg_eclaireurs_per_user} moy/utilisateur` },
                      { label: 'Commentaires', value: reports.kpis.total_eclaireur_comments, sub: '\u00e9clairages re\u00e7us' },
                    ].map((kpi, i) => (
                      <div key={i} data-testid={`kpi-card-${i}`} style={{
                        backgroundColor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)',
                        borderRadius: 12, padding: '20px 16px', textAlign: 'center',
                      }}>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 6 }}>{kpi.label}</p>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 28, color: '#D4AF37', fontWeight: 400, marginBottom: 4 }}>{kpi.value}</p>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.75)' }}>{kpi.sub}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ====== PILOTAGE DES PRIX ====== */}
          {activeSection === 'pricing' && pricing && (
            <div>
              {/* ---- ABONNEMENTS ---- */}
              {sectionTitle('Abonnements')}
              <div className="grid sm:grid-cols-2 gap-6" style={{ marginBottom: 40 }}>
                {Object.entries(pricing.plans).map(([id, info]) => (
                  <div key={id}>
                    <label style={labelStyle}>{info.name}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input data-testid={`admin-price-plan-${id}`} type="number" step="0.01" min="0" value={planPrices[id] ?? ''} onChange={e => setPlanPrices(prev => ({ ...prev, [id]: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300, whiteSpace: 'nowrap' }}>{'\u20AC'} /mois</span>
                    </div>
                    <p style={hintStyle}>D{'\u00e9'}faut : {info.default_price.toFixed(2)} {'\u20AC'}</p>
                  </div>
                ))}
              </div>

              {/* ---- PACKS LIBERTÉ ---- */}
              {sectionTitle('Packs Libert\u00e9 (achat unique)')}
              <div className="grid sm:grid-cols-2 gap-6" style={{ marginBottom: 40 }}>
                {Object.entries(pricing.liberte_packs).map(([id, info]) => (
                  <div key={id} style={{ backgroundColor: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '20px' }}>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 15, color: '#D4AF37', marginBottom: 16 }}>{info.name}</p>
                    <div style={{ marginBottom: 12 }}>
                      <label style={labelStyle}>Prix</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input data-testid={`admin-liberte-price-${id}`} type="number" step="0.01" min="0" value={libertePacks[id]?.price ?? ''} onChange={e => setLibertePacks(prev => ({ ...prev, [id]: { ...prev[id], price: parseFloat(e.target.value) || 0 } }))} style={inputStyle} />
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300 }}>{'\u20AC'}</span>
                      </div>
                      <p style={hintStyle}>D{'\u00e9'}faut : {info.default_price.toFixed(2)} {'\u20AC'}</p>
                    </div>
                    <div>
                      <label style={labelStyle}>Nombre de photos</label>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <input data-testid={`admin-liberte-photos-${id}`} type="number" step="1" min="1" value={libertePacks[id]?.photos ?? ''} onChange={e => setLibertePacks(prev => ({ ...prev, [id]: { ...prev[id], photos: parseInt(e.target.value) || 0 } }))} style={inputStyle} />
                        <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300 }}>photos</span>
                      </div>
                      <p style={hintStyle}>D{'\u00e9'}faut : {info.default_photos} photos</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* ---- OPTIONS RESTAURATION ---- */}
              {sectionTitle('Options Restauration')}
              <div className="grid sm:grid-cols-3 gap-6" style={{ marginBottom: 40 }}>
                {Object.entries(pricing.options).map(([id, info]) => (
                  <div key={id}>
                    <label style={labelStyle}>{info.name}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input data-testid={`admin-option-${id}`} type="number" step="0.01" min="0" value={optionPrices[id] ?? ''} onChange={e => setOptionPrices(prev => ({ ...prev, [id]: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300 }}>{'\u20AC'}</span>
                    </div>
                    <p style={hintStyle}>D{'\u00e9'}faut : {info.default_price.toFixed(2)} {'\u20AC'}</p>
                  </div>
                ))}
                <div>
                  <label style={labelStyle}>Restaurations offertes par Pack Libert{'\u00e9'}</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input data-testid="admin-liberte-free-restorations" type="number" step="1" min="0" value={settings.liberte_free_restorations ?? 5} onChange={e => setSettings(prev => ({ ...prev, liberte_free_restorations: parseInt(e.target.value) || 0 }))} style={inputStyle} />
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300 }}>photos</span>
                  </div>
                  <p style={hintStyle}>D{'\u00e9'}faut : 5 restaurations</p>
                </div>
              </div>

              {/* ---- LIVRES PHOTO ---- */}
              {sectionTitle('Livres Photo')}
              <div className="grid sm:grid-cols-3 gap-6" style={{ marginBottom: 40 }}>
                {Object.entries(pricing.products).map(([id, info]) => (
                  <div key={id}>
                    <label style={labelStyle}>{info.name}</label>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <input data-testid={`admin-price-${id}`} type="number" step="0.01" min="0" value={productPrices[id] ?? ''} onChange={e => setProductPrices(prev => ({ ...prev, [id]: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300 }}>{'\u20AC'}</span>
                    </div>
                    <p style={hintStyle}>D{'\u00e9'}faut : {info.default_price.toFixed(2)} {'\u20AC'}</p>
                  </div>
                ))}
              </div>

              {/* ---- REMISES & FACTURATION ---- */}
              {sectionTitle('R\u00e8gles de Remise & Facturation')}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 20, marginBottom: 40 }}>
                <div>
                  <label style={labelStyle}>H{'\u00e9'}ritage Annuel</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input data-testid="admin-heritage-annual" type="number" step="0.01" min="0" value={settings.heritage_annual_price ?? 48} onChange={e => setSettings(prev => ({ ...prev, heritage_annual_price: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300, whiteSpace: 'nowrap' }}>{'\u20AC'} /an</span>
                  </div>
                  <p style={hintStyle}>D{'\u00e9'}faut : 48,00 {'\u20AC'} /an</p>
                </div>
                <div>
                  <label style={labelStyle}>Remise H{'\u00e9'}ritage sur les Livres</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <input data-testid="admin-lignees-discount" type="number" step="1" min="0" max="100" value={settings.lignees_discount ?? 20} onChange={e => setSettings(prev => ({ ...prev, lignees_discount: parseFloat(e.target.value) || 0 }))} style={inputStyle} />
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(212,175,55,0.35)', fontWeight: 300 }}>%</span>
                  </div>
                  <p style={hintStyle}>D{'\u00e9'}faut : 20%</p>
                </div>
              </div>

              {/* Save bar */}
              <div style={{ position: 'sticky', bottom: 0, padding: '20px 0', backgroundColor: '#000', borderTop: '1px solid rgba(212,175,55,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                {saveMsg && <p data-testid="admin-save-message" style={{ fontFamily: "'Georgia', serif", fontSize: 13, flex: 1, color: saveMsg.type === 'success' ? '#D4AF37' : '#c0392b' }}>{saveMsg.text}</p>}
                <div style={{ display: 'flex', gap: 12, marginLeft: 'auto' }}>
                  <button onClick={() => navigate('/offres')} style={{ fontFamily: "'Georgia', serif", fontSize: 14, padding: '12px 24px', borderRadius: 8, cursor: 'pointer', color: 'rgba(212,175,55,0.6)', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.2)' }}>
                    Voir les offres
                  </button>
                  <button onClick={handleSave} disabled={saving} data-testid="admin-save-button" style={{ fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: 600, padding: '12px 36px', borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', opacity: saving ? 0.6 : 1, boxShadow: '0 4px 20px rgba(212,175,55,0.3)' }}>
                    {saving ? 'Enregistrement...' : 'Enregistrer les prix'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ====== MARKETING & COMMUNICATION ====== */}
          {activeSection === 'marketing' && (
            <div>
              {/* ── 1. MESSAGE DU JOUR (BANDEAU) ── */}
              <div style={{ marginBottom: 40 }}>
                {sectionTitle('Message du Jour (Bandeau)')}
                <div style={{ padding: 24, borderRadius: 12, border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(212,175,55,0.03)' }}>
                  <textarea
                    data-testid="banner-message-input"
                    value={banner.message}
                    onChange={e => setBanner({ ...banner, message: e.target.value })}
                    placeholder="Votre message (ex: Offre de lancement -30% !)"
                    rows={2}
                    style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 14, padding: 12, borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff', resize: 'vertical', marginBottom: 12 }}
                  />
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
                    <label style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.7)' }}>Style :</label>
                    {[
                      { val: 'info', label: 'Dore', color: '#D4AF37' },
                      { val: 'promo', label: 'Vert', color: '#27ae60' },
                      { val: 'urgent', label: 'Rouge', color: '#c0392b' },
                    ].map(s => (
                      <button key={s.val} data-testid={`banner-style-${s.val}`}
                        onClick={() => setBanner({ ...banner, style: s.val })}
                        style={{
                          fontFamily: "'Georgia', serif", fontSize: 12, padding: '4px 14px', borderRadius: 12, cursor: 'pointer',
                          border: banner.style === s.val ? `2px solid ${s.color}` : '1px solid rgba(255,255,255,0.1)',
                          backgroundColor: banner.style === s.val ? `${s.color}15` : 'transparent', color: s.color,
                        }}>{s.label}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                    <input
                      data-testid="banner-link-text"
                      value={banner.link_text || ''}
                      onChange={e => setBanner({ ...banner, link_text: e.target.value })}
                      placeholder="Texte du lien (ex: Voir les offres)"
                      style={{ flex: 1, minWidth: 160, fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                    />
                    <input
                      data-testid="banner-link-url"
                      value={banner.link_url || ''}
                      onChange={e => setBanner({ ...banner, link_url: e.target.value })}
                      placeholder="URL du lien (ex: /offres)"
                      style={{ flex: 1, minWidth: 160, fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                    />
                  </div>
                  {/* Preview */}
                  {banner.message && (
                    <div data-testid="banner-preview" style={{
                      padding: '10px 16px', borderRadius: 8, marginBottom: 16, textAlign: 'center',
                      backgroundColor: banner.style === 'promo' ? 'rgba(39,174,96,0.12)' : banner.style === 'urgent' ? 'rgba(192,57,43,0.12)' : 'rgba(212,175,55,0.08)',
                      border: `1px solid ${banner.style === 'promo' ? 'rgba(39,174,96,0.3)' : banner.style === 'urgent' ? 'rgba(192,57,43,0.3)' : 'rgba(212,175,55,0.2)'}`,
                    }}>
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: banner.style === 'promo' ? '#27ae60' : banner.style === 'urgent' ? '#e74c3c' : '#D4AF37' }}>
                        {banner.message}
                        {banner.link_text && <span style={{ marginLeft: 8, textDecoration: 'underline', fontWeight: 600 }}>{banner.link_text}</span>}
                      </span>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button data-testid="banner-save-btn" onClick={saveBanner} disabled={bannerSaving} style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600, padding: '10px 28px', borderRadius: 8,
                      background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', opacity: bannerSaving ? 0.6 : 1,
                    }}>{bannerSaving ? '...' : 'Publier le bandeau'}</button>
                    <button data-testid="banner-disable-btn" onClick={disableBanner} style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 20px', borderRadius: 8,
                      backgroundColor: 'transparent', color: 'rgba(245,245,220,0.85)', border: '1px solid rgba(255,255,255,0.1)', cursor: 'pointer',
                    }}>Desactiver</button>
                  </div>
                  {bannerMsg && <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#27ae60', marginTop: 8 }}>{bannerMsg}</p>}
                </div>
              </div>

              {/* ── 2. COUPONS STRIPE ── */}
              <div style={{ marginBottom: 40 }}>
                {sectionTitle('Coupons Stripe')}
                <div style={{ padding: 24, borderRadius: 12, border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(212,175,55,0.03)', marginBottom: 16 }}>
                  <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
                    <div style={{ flex: '1 1 140px' }}>
                      <label style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.85)', display: 'block', marginBottom: 4 }}>Nom</label>
                      <input data-testid="coupon-name" value={couponForm.name} onChange={e => setCouponForm({ ...couponForm, name: e.target.value })}
                        placeholder="Ex: LANCEMENT30" style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }} />
                    </div>
                    <div style={{ flex: '0 0 120px' }}>
                      <label style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.85)', display: 'block', marginBottom: 4 }}>Type</label>
                      <select data-testid="coupon-type" value={couponForm.discount_type} onChange={e => setCouponForm({ ...couponForm, discount_type: e.target.value })}
                        style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}>
                        <option value="percent">Pourcentage</option>
                        <option value="fixed">Montant fixe</option>
                      </select>
                    </div>
                    <div style={{ flex: '0 0 100px' }}>
                      <label style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.85)', display: 'block', marginBottom: 4 }}>{couponForm.discount_type === 'percent' ? 'Remise (%)' : 'Montant (EUR)'}</label>
                      <input data-testid="coupon-value" type="number" value={couponForm.discount_value} onChange={e => setCouponForm({ ...couponForm, discount_value: e.target.value })}
                        placeholder={couponForm.discount_type === 'percent' ? '20' : '5'} style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }} />
                    </div>
                    <div style={{ flex: '0 0 120px' }}>
                      <label style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.85)', display: 'block', marginBottom: 4 }}>Duree</label>
                      <select data-testid="coupon-duration" value={couponForm.duration} onChange={e => setCouponForm({ ...couponForm, duration: e.target.value })}
                        style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.5)', color: '#fff' }}>
                        <option value="once">Usage unique</option>
                        <option value="repeating">Recurrent</option>
                        <option value="forever">Permanent</option>
                      </select>
                    </div>
                    <div style={{ flex: '0 0 80px' }}>
                      <label style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.85)', display: 'block', marginBottom: 4 }}>Max uses</label>
                      <input data-testid="coupon-max" type="number" value={couponForm.max_redemptions} onChange={e => setCouponForm({ ...couponForm, max_redemptions: e.target.value })}
                        placeholder="illimite" style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }} />
                    </div>
                    <button data-testid="coupon-create-btn" onClick={createCoupon} disabled={couponSaving || !couponForm.name || !couponForm.discount_value} style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600, padding: '8px 20px', borderRadius: 8,
                      background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', opacity: (couponSaving || !couponForm.name) ? 0.5 : 1, whiteSpace: 'nowrap',
                    }}>{couponSaving ? '...' : 'Creer'}</button>
                  </div>
                </div>
                {/* Coupons list */}
                {coupons.length > 0 && (
                  <div style={{ borderRadius: 12, border: '1px solid rgba(212,175,55,0.1)', overflow: 'hidden' }}>
                    {coupons.map((c, i) => (
                      <div key={c.id} data-testid={`coupon-row-${c.id}`} style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px',
                        borderBottom: i < coupons.length - 1 ? '1px solid rgba(212,175,55,0.06)' : 'none',
                        backgroundColor: c.valid ? 'transparent' : 'rgba(255,255,255,0.02)',
                      }}>
                        <div>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: c.valid ? '#D4AF37' : 'rgba(255,255,255,0.3)', fontWeight: 600 }}>{c.name}</span>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)', marginLeft: 10 }}>
                            {c.percent_off ? `${c.percent_off}%` : `${(c.amount_off / 100).toFixed(2)} ${(c.currency || 'EUR').toUpperCase()}`}
                          </span>
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.75)', marginLeft: 10 }}>
                            {c.times_redeemed || 0}{c.max_redemptions ? `/${c.max_redemptions}` : ''} utilise(s)
                          </span>
                        </div>
                        <button onClick={() => deleteCoupon(c.id)} style={{
                          fontFamily: "'Georgia', serif", fontSize: 11, padding: '4px 12px', borderRadius: 6,
                          backgroundColor: 'rgba(192,57,43,0.1)', color: '#c0392b', border: '1px solid rgba(192,57,43,0.2)', cursor: 'pointer',
                        }}>Supprimer</button>
                      </div>
                    ))}
                  </div>
                )}
                {coupons.length === 0 && (
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.75)', textAlign: 'center', padding: 20 }}>
                    Aucun coupon actif. Creez votre premier coupon Stripe ci-dessus.
                  </p>
                )}
              </div>

              {/* ── 3. LE MURMURE DE LA BOBINE ── */}
              <div style={{ marginBottom: 40 }}>
                {sectionTitle('Le Murmure de la Bobine')}
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.85)', marginBottom: 16, marginTop: -8 }}>
                  Notifications douces qui invitent vos utilisateurs a replonger dans leurs souvenirs.
                </p>
                <div style={{ padding: 24, borderRadius: 12, border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(212,175,55,0.03)', marginBottom: 20 }}>
                  <textarea
                    data-testid="murmure-text-input"
                    value={murmureText}
                    onChange={e => setMurmureText(e.target.value)}
                    placeholder="Redigez un murmure (ex: Es-tu dans le metro ? Que dirais-tu de replonger dans cet album ?)"
                    rows={2}
                    style={{ width: '100%', fontFamily: "'Georgia', serif", fontSize: 14, padding: 12, borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff', resize: 'vertical', marginBottom: 12 }}
                  />
                  <div style={{ display: 'flex', gap: 12 }}>
                    <button data-testid="murmure-send-btn" onClick={() => createMurmure(true)} disabled={murmureSending || !murmureText.trim()} style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600, padding: '10px 24px', borderRadius: 8,
                      background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', opacity: (!murmureText.trim() || murmureSending) ? 0.5 : 1,
                    }}>{murmureSending ? '...' : 'Envoyer maintenant'}</button>
                    <button data-testid="murmure-save-btn" onClick={() => createMurmure(false)} disabled={murmureSending || !murmureText.trim()} style={{
                      fontFamily: "'Georgia', serif", fontSize: 13, padding: '10px 20px', borderRadius: 8,
                      backgroundColor: 'transparent', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.3)', cursor: 'pointer', opacity: !murmureText.trim() ? 0.5 : 1,
                    }}>Sauvegarder (sans envoyer)</button>
                  </div>
                </div>

                {/* Murmures library - loaded from backend */}
                <MurmuresLibrary adminKey={adminKey} murmureText={murmureText} setMurmureText={setMurmureText} />

                {/* Auto config */}
                <div style={{ padding: 20, borderRadius: 12, border: '1px solid rgba(212,175,55,0.1)', backgroundColor: 'rgba(212,175,55,0.02)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: '#D4AF37', fontWeight: 600 }}>Automatisation</span>
                    <button data-testid="auto-toggle" onClick={() => setAutoConfig({ ...autoConfig, enabled: !autoConfig.enabled })} style={{
                      fontFamily: "'Georgia', serif", fontSize: 11, padding: '4px 14px', borderRadius: 12, cursor: 'pointer',
                      backgroundColor: autoConfig.enabled ? 'rgba(39,174,96,0.15)' : 'rgba(255,255,255,0.05)',
                      color: autoConfig.enabled ? '#27ae60' : 'rgba(255,255,255,0.4)',
                      border: `1px solid ${autoConfig.enabled ? 'rgba(39,174,96,0.3)' : 'rgba(255,255,255,0.1)'}`,
                    }}>{autoConfig.enabled ? 'Active' : 'Desactive'}</button>
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)' }}>
                      tous les
                    </span>
                    <input data-testid="auto-frequency" type="number" value={autoConfig.frequency_days} onChange={e => setAutoConfig({ ...autoConfig, frequency_days: parseInt(e.target.value) || 7 })}
                      style={{ width: 50, fontFamily: "'Georgia', serif", fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.2)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff', textAlign: 'center' }} />
                    <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)' }}>jours</span>
                  </div>
                  <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)', marginBottom: 12 }}>
                    Messages automatiques (un sera choisi au hasard) :
                  </p>
                  {autoConfig.messages.map((msg, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                      <input value={msg} onChange={e => { const m = [...autoConfig.messages]; m[i] = e.target.value; setAutoConfig({ ...autoConfig, messages: m }); }}
                        style={{ flex: 1, fontFamily: "'Georgia', serif", fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }} />
                      <button onClick={() => { const m = autoConfig.messages.filter((_, j) => j !== i); setAutoConfig({ ...autoConfig, messages: m }); }}
                        style={{ fontSize: 14, color: 'rgba(192,57,43,0.6)', background: 'none', border: 'none', cursor: 'pointer', padding: '0 4px' }}>x</button>
                    </div>
                  ))}
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <input data-testid="auto-new-msg" value={autoMsg} onChange={e => setAutoMsg(e.target.value)} placeholder="Ajouter un message automatique..."
                      style={{ flex: 1, fontFamily: "'Georgia', serif", fontSize: 12, padding: '6px 10px', borderRadius: 6, border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(0,0,0,0.3)', color: '#fff' }}
                      onKeyDown={e => { if (e.key === 'Enter' && autoMsg.trim()) { setAutoConfig({ ...autoConfig, messages: [...autoConfig.messages, autoMsg.trim()] }); setAutoMsg(''); } }} />
                    <button onClick={() => { if (autoMsg.trim()) { setAutoConfig({ ...autoConfig, messages: [...autoConfig.messages, autoMsg.trim()] }); setAutoMsg(''); } }}
                      style={{ fontFamily: "'Georgia', serif", fontSize: 11, padding: '6px 12px', borderRadius: 6, backgroundColor: 'rgba(212,175,55,0.1)', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.2)', cursor: 'pointer' }}>+</button>
                  </div>
                  <button data-testid="auto-save-btn" onClick={saveAutoConfig} disabled={autoSaving} style={{
                    fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600, padding: '10px 24px', borderRadius: 8, marginTop: 16,
                    background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none', cursor: 'pointer', opacity: autoSaving ? 0.6 : 1,
                  }}>{autoSaving ? '...' : 'Sauvegarder l\'automatisation'}</button>
                </div>
              </div>
            </div>
          )}

          {/* ====== L'UNIVERS BOBINE ====== */}
          {activeSection === 'univers' && (
            <div data-testid="admin-univers-section">
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 22, color: '#D4AF37', fontWeight: 500, marginBottom: 6, letterSpacing: 0.5 }}>
                  Gestion de l'Univers Bobine
                </h2>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(247,230,189,0.8)', fontStyle: 'italic', margin: 0 }}>
                  Modifiez, ajoutez ou supprimez les contenus visibles sur la page publique <code style={{ color: '#D4AF37' }}>/univers</code>.
                </p>
              </div>
              <UniversAdminTab adminKey={adminKey} />
            </div>
          )}

          {/* ====== TÉMOIGNAGES (modération) ====== */}
          {activeSection === 'temoignages' && (
            <div data-testid="admin-temoignages-tab">
              <div style={{ marginBottom: 28 }}>
                <h2 style={{ fontFamily: "'Georgia', serif", fontSize: 22, color: '#D4AF37', fontWeight: 500, marginBottom: 6, letterSpacing: 0.5 }}>
                  Modération des Témoignages
                </h2>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(247,230,189,0.8)', fontStyle: 'italic', margin: 0 }}>
                  Approuvez, dépubliez ou supprimez les témoignages soumis par les visiteurs. Les témoignages "publiés" apparaissent sur la page d'accueil.
                </p>
              </div>
              <TestimonialsAdminTab adminKey={adminKey} />
            </div>
          )}

          {/* ====== AUTOMATES (Schedulers) ====== */}
          {activeSection === 'automates' && (
            <div data-testid="admin-automates-section">
              {sectionTitle('T\u00e9l\u00e9commande des Automates')}

              {/* Notice tutoriel */}
              <div data-testid="automates-tutorial" style={{ marginBottom: 28, padding: '20px 24px', borderRadius: 12, border: '1px solid rgba(212,175,55,0.12)', background: 'rgba(212,175,55,0.03)' }}>
                <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontWeight: 600, marginBottom: 10 }}>Guide rapide</p>
                <div style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.7)', lineHeight: 1.9 }}>
                  <p style={{ marginBottom: 6 }}><strong style={{ color: 'rgba(212,175,55,0.8)' }}>Murmures</strong> : Modifiez la fr{'\u00e9'}quence ci-dessous (ex: 7 jours). Pour d{'\u00e9'}sactiver, allez dans Marketing {'\u2192'} Automatisation {'\u2192'} cliquez sur "D{'\u00e9'}sactiv{'\u00e9'}".</p>
                  <p style={{ marginBottom: 6 }}><strong style={{ color: 'rgba(212,175,55,0.8)' }}>Alertes Sas</strong> : R{'\u00e9'}glez le d{'\u00e9'}lai (ex: 3 jours avant expiration). L'email unifi{'\u00e9'} (Sas + Corbeille) sera envoy{'\u00e9'} automatiquement.</p>
                  <p style={{ marginBottom: 6 }}><strong style={{ color: 'rgba(212,175,55,0.8)' }}>Relance inactifs</strong> : D{'\u00e9'}finissez apr{'\u00e8'}s combien de jours d'inactivit{'\u00e9'} un email de r{'\u00e9'}engagement est envoy{'\u00e9'}.</p>
                  <p><strong style={{ color: 'rgba(212,175,55,0.8)' }}>Max emails</strong> : Plafond mensuel par utilisateur (hors emails de paiement et s{'\u00e9'}curit{'\u00e9'}).</p>
                </div>
              </div>

              <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)', marginBottom: 24, textAlign: 'center' }}>
                Ces r{'\u00e9'}glages contr{'\u00f4'}lent directement les automates. Chaque modification est appliqu{'\u00e9'}e en temps r{'\u00e9'}el.
              </p>

              {/* Settings editables */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 32 }}>
                {[
                  { key: 'sas_alert_days', label: 'Alerte Sas (jours avant)', desc: 'Email unifie Sas + Poubelle', suffix: 'jours' },
                  { key: 'murmures_frequency_days', label: 'Frequence Murmures', desc: 'Intervalle entre les murmures', suffix: 'jours' },
                  { key: 'engagement_inactive_days', label: 'Relance inactifs apres', desc: 'Delai avant relance gratuits', suffix: 'jours' },
                  { key: 'max_emails_per_month', label: 'Max emails / mois', desc: 'Hors paiements et securite', suffix: 'emails' },
                ].map(s => (
                  <div key={s.key} style={{ background: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '20px' }}>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', margin: '0 0 4px', fontWeight: 500 }}>{s.label}</p>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.75)', margin: '0 0 12px' }}>{s.desc}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <input data-testid={`setting-${s.key}`} type="number" min="1" max="90"
                        value={schedulerSettings[s.key] || ''} onChange={e => setSchedulerSettings(prev => ({ ...prev, [s.key]: parseInt(e.target.value) || 0 }))}
                        style={{ width: 70, padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(212,175,55,0.2)', background: 'rgba(0,0,0,0.3)', color: '#D4AF37', fontFamily: "'Georgia', serif", fontSize: 16, fontWeight: 600, textAlign: 'center' }} />
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.85)' }}>{s.suffix}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ textAlign: 'center', marginBottom: 32 }}>
                <button data-testid="save-scheduler-settings" onClick={saveSchedulerSettings} disabled={schedulerSaving}
                  style={{ fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: 600, padding: '12px 36px', borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none' }}>
                  {schedulerSaving ? 'Sauvegarde...' : 'Appliquer les r\u00e9glages'}
                </button>
                {schedulerMsg && <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: '#D4AF37', marginTop: 8 }}>{schedulerMsg}</p>}
              </div>

              {/* Scheduler status */}
              {sectionTitle('Etat des Automates')}
              {schedulerData?.schedulers ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  {schedulerData.schedulers.map((s, i) => (
                    <div key={i} data-testid={`scheduler-${s.name}`} style={{ background: 'rgba(212,175,55,0.03)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '16px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: s.active ? '#27ae60' : '#e74c3c', display: 'inline-block' }} />
                          <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: '#D4AF37', fontWeight: 500 }}>{s.name.replace(/_/g, ' ')}</span>
                        </div>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.85)', margin: 0 }}>{s.description}</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(212,175,55,0.5)', margin: 0 }}>{s.interval}</p>
                        {s.alerts_sent !== undefined && (
                          <p style={{ fontFamily: "'Georgia', serif", fontSize: 11, color: 'rgba(245,245,220,0.75)', margin: '4px 0 0' }}>{s.alerts_sent} alertes envoyees</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ fontFamily: "'Georgia', serif", color: 'rgba(212,175,55,0.4)', textAlign: 'center' }}>Chargement...</p>
              )}

              {/* Explication technique */}
              <div style={{ marginTop: 32, padding: '16px 20px', borderRadius: 12, border: '1px solid rgba(212,175,55,0.08)', background: 'rgba(0,0,0,0.2)' }}>
                {sectionTitle('Comment ca fonctionne')}
                <div style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)', lineHeight: 1.8 }}>
                  <p>Chaque automate est un processus qui tourne en arriere-plan. Quand vous modifiez un reglage ici, la valeur est enregistree en base de donnees (MongoDB). A chaque cycle, l'automate relit ces valeurs avant de s'executer.</p>
                  <p style={{ marginTop: 8 }}>Exemple : si vous passez l'alerte Sas de 3 a 5 jours, le prochain cycle enverra l'email 5 jours avant l'expiration au lieu de 3.</p>
                  <p style={{ marginTop: 8 }}>Les prix modifies dans "Pilotage des Prix" sont envoyes directement a Stripe via son API. La modification est instantanee.</p>
                </div>
              </div>
            </div>
          )}

          {/* ====== STOCKAGE & CHIFFREMENT ====== */}
          {activeSection === 'storage' && (
            <div>
              {!storageStatus ? (
                <p style={{ fontFamily: "'Georgia', serif", color: 'rgba(212,175,55,0.4)', textAlign: 'center' }}>Chargement...</p>
              ) : (
                <>
                  {/* Status card */}
                  <div data-testid="storage-status-card" style={{
                    textAlign: 'center', marginBottom: 40, padding: '36px 24px',
                    background: storageStatus.s3_connected
                      ? 'linear-gradient(135deg, rgba(39,174,96,0.08), rgba(39,174,96,0.02))'
                      : 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(212,175,55,0.02))',
                    border: `1px solid ${storageStatus.s3_connected ? 'rgba(39,174,96,0.3)' : 'rgba(212,175,55,0.2)'}`,
                    borderRadius: 16,
                  }}>
                    <div style={{
                      display: 'inline-flex', alignItems: 'center', gap: 10, marginBottom: 16,
                      padding: '8px 20px', borderRadius: 20,
                      backgroundColor: storageStatus.s3_connected ? 'rgba(39,174,96,0.15)' : 'rgba(212,175,55,0.08)',
                    }}>
                      <div style={{
                        width: 10, height: 10, borderRadius: '50%',
                        backgroundColor: storageStatus.s3_connected ? '#27ae60' : '#f39c12',
                      }} />
                      <span style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 600,
                        color: storageStatus.s3_connected ? '#27ae60' : '#f39c12',
                      }}>
                        {storageStatus.s3_connected ? (storageStatus.storage_provider || 'Infomaniak kDrive (Suisse)') : 'Stockage Local (MongoDB)'}
                      </span>
                    </div>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 14, color: 'rgba(245,245,220,0.7)', marginBottom: 8 }}>
                      Chiffrement : <strong style={{ color: storageStatus.encryption !== 'none' ? '#27ae60' : 'rgba(255,255,255,0.5)' }}>
                        {storageStatus.encryption !== 'none' ? 'AES-256-GCM' : 'Non actif'}
                      </strong>
                    </p>
                    <p style={{ fontFamily: "'Georgia', serif", fontSize: 12, color: 'rgba(245,245,220,0.75)' }}>
                      {storageStatus.s3_connected
                        ? 'Vos photos sont chiffr\u00e9es (AES-256) avant d\u2019\u00eatre stock\u00e9es sur kDrive Infomaniak en Suisse.'
                        : 'Configurez les cl\u00e9s Infomaniak dans le fichier .env pour activer le stockage chiffr\u00e9 en Suisse.'}
                    </p>
                  </div>

                  {/* Photo distribution */}
                  <div className="grid sm:grid-cols-3 gap-5" style={{ marginBottom: 36 }}>
                    <div data-testid="storage-total" style={{ backgroundColor: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.12)', borderRadius: 12, padding: '24px 20px' }}>
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Total Photos</p>
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 32, color: '#D4AF37', fontWeight: 400 }}>{storageStatus.photos_total}</p>
                    </div>
                    <div data-testid="storage-s3" style={{ backgroundColor: 'rgba(39,174,96,0.04)', border: '1px solid rgba(39,174,96,0.12)', borderRadius: 12, padding: '24px 20px' }}>
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#27ae60', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Chiffr{'\u00e9'}es (kDrive)</p>
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 32, color: '#27ae60', fontWeight: 400 }}>{storageStatus.photos_s3}</p>
                    </div>
                    <div data-testid="storage-mongo" style={{ backgroundColor: 'rgba(243,156,18,0.04)', border: '1px solid rgba(243,156,18,0.12)', borderRadius: 12, padding: '24px 20px' }}>
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, fontWeight: 700, color: '#f39c12', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 8 }}>Locales (MongoDB)</p>
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 32, color: '#f39c12', fontWeight: 400 }}>{storageStatus.photos_mongo}</p>
                    </div>
                  </div>

                  {/* Migration */}
                  {storageStatus.s3_connected && storageStatus.photos_mongo > 0 && (
                    <div style={{
                      padding: '24px', borderRadius: 12,
                      border: '1px solid rgba(212,175,55,0.15)',
                      backgroundColor: 'rgba(212,175,55,0.03)',
                      marginBottom: 24,
                    }}>
                      {sectionTitle('Migration vers kDrive Chiffre')}
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.7)', marginBottom: 16 }}>
                        {storageStatus.photos_mongo} photo(s) sont encore en stockage local.
                        Lancez la migration pour les chiffrer et les transf{'\u00e9'}rer vers kDrive Infomaniak.
                      </p>
                      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                        <button data-testid="migrate-btn" onClick={() => handleMigrate(false)} disabled={migrating} style={{
                          fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: 600,
                          padding: '12px 36px', borderRadius: 8, cursor: 'pointer',
                          backgroundColor: 'transparent', color: '#D4AF37', border: '1px solid rgba(212,175,55,0.3)',
                          opacity: migrating ? 0.6 : 1,
                        }}>
                          {migrating ? 'Migration en cours...' : 'Migrer 20 photos'}
                        </button>
                        <button data-testid="migrate-all-btn" onClick={() => handleMigrate(true)} disabled={migrating} style={{
                          fontFamily: "'Georgia', serif", fontSize: 14, fontWeight: 600,
                          padding: '12px 36px', borderRadius: 8, cursor: 'pointer',
                          background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none',
                          opacity: migrating ? 0.6 : 1,
                        }}>
                          {migrating ? 'Migration en cours...' : `Tout migrer (${storageStatus.photos_mongo})`}
                        </button>
                      </div>
                      {migrateResult && (
                        <p data-testid="migrate-result" style={{
                          fontFamily: "'Georgia', serif", fontSize: 13, marginTop: 12,
                          color: migrateResult.status === 'error' ? '#c0392b' : '#D4AF37',
                        }}>
                          {migrateResult.status === 'error'
                            ? migrateResult.message
                            : migrateResult.running
                              ? `Migration en cours : ${migrateResult.migrated || 0} / ${migrateResult.total || '?'} (${migrateResult.remaining_in_db || '?'} restante(s) en local)`
                              : migrateResult.status === 'started'
                                ? `Migration lancée : ${migrateResult.total_to_migrate} photo(s) vers ${migrateResult.destination}`
                                : `Terminé : ${migrateResult.migrated} migrée(s), ${migrateResult.remaining_in_db ?? 0} restante(s)`}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Config helper - only show if NO cloud storage configured */}
                  {!storageStatus.s3_configured && !storageStatus.kdrive_configured && (
                    <div style={{
                      padding: '24px', borderRadius: 12,
                      border: '1px solid rgba(212,175,55,0.15)',
                      backgroundColor: 'rgba(212,175,55,0.03)',
                    }}>
                      {sectionTitle('Configuration requise')}
                      <p style={{ fontFamily: "'Georgia', serif", fontSize: 13, color: 'rgba(245,245,220,0.7)', marginBottom: 12 }}>
                        Ajoutez ces variables dans <code style={{ color: '#D4AF37' }}>backend/.env</code> :
                      </p>
                      <pre style={{
                        fontFamily: 'monospace', fontSize: 12, padding: 16, borderRadius: 8,
                        backgroundColor: 'rgba(0,0,0,0.5)', color: 'rgba(212,175,55,0.7)',
                        overflowX: 'auto', lineHeight: 1.6,
                      }}>{`S3_ENDPOINT=https://s3.pub1.infomaniak.cloud
S3_ACCESS_KEY=votre_access_key
S3_SECRET_KEY=votre_secret_key
S3_BUCKET=bobine-photos
ENCRYPTION_KEY=${'{64 caracteres hex}'}`}</pre>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

        </div>
      </main>
    </div>
  );
};

export default AdminPage;
